import { ActionIcon, Button, Divider, Group, Menu, Paper, Tooltip } from '@mantine/core'
import { IconChevronDown, IconTrash, IconX } from '@tabler/icons-react'
import { BOARD_PRIORITY_VALUES, BOARD_STATUS_VALUES } from '@behuman/core-contract'

type BulkActionBarProps = {
  count: number
  canSetStatus: boolean
  canSetPriority: boolean
  pending: boolean
  onSetStatus: (status: string) => void
  onSetPriority: (priority: string) => void
  onDelete: () => void
  onClear: () => void
}

const SetMenu = ({
  label,
  values,
  disabled,
  onSelect,
}: {
  label: string
  values: readonly string[]
  disabled: boolean
  onSelect: (value: string) => void
}) => (
  <Menu shadow="md" position="top" withinPortal>
    <Menu.Target>
      <Button
        size="xs"
        variant="subtle"
        color="gray"
        rightSection={<IconChevronDown size={14} />}
        disabled={disabled}
      >
        {label}
      </Button>
    </Menu.Target>
    <Menu.Dropdown>
      {values.map((value) => (
        <Menu.Item key={value} onClick={() => onSelect(value)}>
          {value}
        </Menu.Item>
      ))}
    </Menu.Dropdown>
  </Menu>
)

// Floating command bar shown while one or more tasks are selected. A Mantine
// Group keeps everything on one row; it floats above the StickyScrollArea's
// bottom scrollbar (fixed bottom-0, h-4) so the two never overlap.
export const BulkActionBar = ({
  count,
  canSetStatus,
  canSetPriority,
  pending,
  onSetStatus,
  onSetPriority,
  onDelete,
  onClear,
}: BulkActionBarProps) => (
  <Paper
    shadow="md"
    withBorder
    radius="xl"
    className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2"
  >
    <Group gap="xs" wrap="nowrap" className="py-1.5 pr-2 pl-2">
      <span className="rounded-full bg-[#275587] px-2.5 py-1 text-xs font-semibold text-white">
        {count} selected
      </span>
      <Divider orientation="vertical" />
      {canSetStatus && (
        <SetMenu
          label="Status"
          values={BOARD_STATUS_VALUES}
          disabled={pending}
          onSelect={onSetStatus}
        />
      )}
      {canSetPriority && (
        <SetMenu
          label="Priority"
          values={BOARD_PRIORITY_VALUES}
          disabled={pending}
          onSelect={onSetPriority}
        />
      )}
      <Button
        size="xs"
        variant="subtle"
        color="red"
        leftSection={<IconTrash size={14} />}
        disabled={pending}
        onClick={onDelete}
      >
        Delete
      </Button>
      <Divider orientation="vertical" />
      <Tooltip label="Clear selection" withArrow>
        <ActionIcon variant="subtle" color="gray" aria-label="Clear selection" onClick={onClear}>
          <IconX size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  </Paper>
)
